import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

export interface SettingsExperienceCopy {
  devices: string;
  invoices: string;
  personal: string;
  workspaceScope: string;
  preferences: string;
  general: string;
  membersTeams: string;
  permissions: string;
  fileAccess: string;
  myMemories: string;
  sharedMemories: string;
  overview: string;
  creditActivity: string;
  planCredits: string;
  assistant: string;
  memberships: string;
  notifications: string;
  openNavigation: string;
  noAccess: string;
}

const COPY: Record<SupportedLocale, SettingsExperienceCopy> = {
  en: {
    devices: 'Devices',
    invoices: 'Invoices',
    personal: 'Personal',
    workspaceScope: 'Workspace',
    preferences: 'Preferences',
    general: 'General',
    membersTeams: 'Members & teams',
    permissions: 'Permissions',
    fileAccess: 'File access',
    myMemories: 'My memories',
    sharedMemories: 'Shared memories',
    overview: 'Overview',
    creditActivity: 'Credit activity',
    planCredits: 'Plan & credits',
    assistant: 'Assistant',
    memberships: 'Memberships',
    notifications: 'Notifications',
    openNavigation: 'Open settings navigation',
    noAccess: 'You don’t have access to these settings.',
  },
  'pt-BR': {
    devices: 'Dispositivos',
    invoices: 'Faturas',
    personal: 'Pessoal',
    workspaceScope: 'Espaço de trabalho',
    preferences: 'Preferências',
    general: 'Geral',
    membersTeams: 'Membros e equipes',
    permissions: 'Permissões',
    fileAccess: 'Acesso a arquivos',
    myMemories: 'Minhas memórias',
    sharedMemories: 'Memórias compartilhadas',
    overview: 'Visão geral',
    creditActivity: 'Atividade de créditos',
    planCredits: 'Plano e créditos',
    assistant: 'Assistente',
    memberships: 'Participações',
    notifications: 'Notificações',
    openNavigation: 'Abrir navegação de configurações',
    noAccess: 'Você não tem acesso a estas configurações.',
  },
  ar: {
    devices: 'الأجهزة',
    invoices: 'الفواتير',
    personal: 'شخصي',
    workspaceScope: 'مساحة العمل',
    preferences: 'التفضيلات',
    general: 'عام',
    membersTeams: 'الأعضاء والفرق',
    permissions: 'الأذونات',
    fileAccess: 'الوصول إلى الملفات',
    myMemories: 'ذكرياتي',
    sharedMemories: 'الذكريات المشتركة',
    overview: 'نظرة عامة',
    creditActivity: 'نشاط الأرصدة',
    planCredits: 'الخطة والأرصدة',
    assistant: 'المساعد',
    memberships: 'العضويات',
    notifications: 'الإشعارات',
    openNavigation: 'فتح التنقل في الإعدادات',
    noAccess: 'ليس لديك صلاحية الوصول إلى هذه الإعدادات.',
  },
  de: {
    devices: 'Geräte',
    invoices: 'Rechnungen',
    personal: 'Persönlich',
    workspaceScope: 'Arbeitsbereich',
    preferences: 'Einstellungen',
    general: 'Allgemein',
    membersTeams: 'Mitglieder & Teams',
    permissions: 'Berechtigungen',
    fileAccess: 'Dateizugriff',
    myMemories: 'Meine Erinnerungen',
    sharedMemories: 'Geteilte Erinnerungen',
    overview: 'Übersicht',
    creditActivity: 'Guthabenaktivität',
    planCredits: 'Tarif & Guthaben',
    assistant: 'Assistent',
    memberships: 'Mitgliedschaften',
    notifications: 'Benachrichtigungen',
    openNavigation: 'Einstellungsnavigation öffnen',
    noAccess: 'Du hast keinen Zugriff auf diese Einstellungen.',
  },
  es: {
    devices: 'Dispositivos',
    invoices: 'Facturas',
    personal: 'Personal',
    workspaceScope: 'Espacio de trabajo',
    preferences: 'Preferencias',
    general: 'General',
    membersTeams: 'Miembros y equipos',
    permissions: 'Permisos',
    fileAccess: 'Acceso a archivos',
    myMemories: 'Mis recuerdos',
    sharedMemories: 'Recuerdos compartidos',
    overview: 'Resumen',
    creditActivity: 'Actividad de créditos',
    planCredits: 'Plan y créditos',
    assistant: 'Asistente',
    memberships: 'Membresías',
    notifications: 'Notificaciones',
    openNavigation: 'Abrir navegación de ajustes',
    noAccess: 'No tienes acceso a estos ajustes.',
  },
  fr: {
    devices: 'Appareils',
    invoices: 'Factures',
    personal: 'Personnel',
    workspaceScope: 'Espace de travail',
    preferences: 'Préférences',
    general: 'Général',
    membersTeams: 'Membres et équipes',
    permissions: 'Autorisations',
    fileAccess: 'Accès aux fichiers',
    myMemories: 'Mes souvenirs',
    sharedMemories: 'Souvenirs partagés',
    overview: 'Vue d’ensemble',
    creditActivity: 'Activité des crédits',
    planCredits: 'Offre et crédits',
    assistant: 'Assistant',
    memberships: 'Adhésions',
    notifications: 'Notifications',
    openNavigation: 'Ouvrir la navigation des paramètres',
    noAccess: 'Vous n’avez pas accès à ces paramètres.',
  },
  it: {
    devices: 'Dispositivi',
    invoices: 'Fatture',
    personal: 'Personale',
    workspaceScope: 'Spazio di lavoro',
    preferences: 'Preferenze',
    general: 'Generale',
    membersTeams: 'Membri e team',
    permissions: 'Autorizzazioni',
    fileAccess: 'Accesso ai file',
    myMemories: 'I miei ricordi',
    sharedMemories: 'Ricordi condivisi',
    overview: 'Panoramica',
    creditActivity: 'Attività dei crediti',
    planCredits: 'Piano e crediti',
    assistant: 'Assistente',
    memberships: 'Iscrizioni',
    notifications: 'Notifiche',
    openNavigation: 'Apri navigazione delle impostazioni',
    noAccess: 'Non hai accesso a queste impostazioni.',
  },
  ko: {
    devices: '기기',
    invoices: '청구서',
    personal: '개인',
    workspaceScope: '워크스페이스',
    preferences: '환경설정',
    general: '일반',
    membersTeams: '멤버 및 팀',
    permissions: '권한',
    fileAccess: '파일 접근',
    myMemories: '내 기억',
    sharedMemories: '공유 기억',
    overview: '개요',
    creditActivity: '크레딧 활동',
    planCredits: '요금제 및 크레딧',
    assistant: '어시스턴트',
    memberships: '멤버십',
    notifications: '알림',
    openNavigation: '설정 탐색 열기',
    noAccess: '이 설정에 접근할 권한이 없습니다.',
  },
  ja: {
    devices: 'デバイス',
    invoices: '請求書',
    personal: '個人',
    workspaceScope: 'ワークスペース',
    preferences: '環境設定',
    general: '一般',
    membersTeams: 'メンバーとチーム',
    permissions: '権限',
    fileAccess: 'ファイルアクセス',
    myMemories: '自分のメモリー',
    sharedMemories: '共有メモリー',
    overview: '概要',
    creditActivity: 'クレジットの利用履歴',
    planCredits: 'プランとクレジット',
    assistant: 'アシスタント',
    memberships: '参加ワークスペース',
    notifications: '通知',
    openNavigation: '設定ナビゲーションを開く',
    noAccess: 'これらの設定にアクセスする権限がありません。',
  },
  hi: {
    devices: 'डिवाइस',
    invoices: 'चालान',
    personal: 'व्यक्तिगत',
    workspaceScope: 'वर्कस्पेस',
    preferences: 'प्राथमिकताएँ',
    general: 'सामान्य',
    membersTeams: 'सदस्य और टीमें',
    permissions: 'अनुमतियाँ',
    fileAccess: 'फ़ाइल पहुँच',
    myMemories: 'मेरी यादें',
    sharedMemories: 'साझा यादें',
    overview: 'अवलोकन',
    creditActivity: 'क्रेडिट गतिविधि',
    planCredits: 'प्लान और क्रेडिट',
    assistant: 'सहायक',
    memberships: 'सदस्यताएँ',
    notifications: 'सूचनाएँ',
    openNavigation: 'सेटिंग नेविगेशन खोलें',
    noAccess: 'आपको इन सेटिंग तक पहुँच नहीं है।',
  },
  ru: {
    devices: 'Устройства',
    invoices: 'Счета',
    personal: 'Личное',
    workspaceScope: 'Рабочее пространство',
    preferences: 'Предпочтения',
    general: 'Общие',
    membersTeams: 'Участники и команды',
    permissions: 'Разрешения',
    fileAccess: 'Доступ к файлам',
    myMemories: 'Мои воспоминания',
    sharedMemories: 'Общие воспоминания',
    overview: 'Обзор',
    creditActivity: 'Операции с кредитами',
    planCredits: 'Тариф и кредиты',
    assistant: 'Ассистент',
    memberships: 'Участие',
    notifications: 'Уведомления',
    openNavigation: 'Открыть навигацию настроек',
    noAccess: 'У вас нет доступа к этим настройкам.',
  },
  zh: {
    devices: '设备',
    invoices: '发票',
    personal: '个人',
    workspaceScope: '工作区',
    preferences: '偏好设置',
    general: '常规',
    membersTeams: '成员与团队',
    permissions: '权限',
    fileAccess: '文件访问',
    myMemories: '我的记忆',
    sharedMemories: '共享记忆',
    overview: '概览',
    creditActivity: '积分活动',
    planCredits: '套餐与积分',
    assistant: '助手',
    memberships: '成员资格',
    notifications: '通知',
    openNavigation: '打开设置导航',
    noAccess: '你无权访问这些设置。',
  },
  'zh-TW': {
    devices: '裝置',
    invoices: '發票',
    personal: '個人',
    workspaceScope: '工作區',
    preferences: '偏好設定',
    general: '一般',
    membersTeams: '成員與團隊',
    permissions: '權限',
    fileAccess: '檔案存取',
    myMemories: '我的記憶',
    sharedMemories: '共享記憶',
    overview: '概覽',
    creditActivity: '點數活動',
    planCredits: '方案與點數',
    assistant: '助理',
    memberships: '成員資格',
    notifications: '通知',
    openNavigation: '開啟設定導覽',
    noAccess: '你無權存取這些設定。',
  },
  vi: {
    devices: 'Thiết bị',
    invoices: 'Hóa đơn',
    personal: 'Cá nhân',
    workspaceScope: 'Không gian làm việc',
    preferences: 'Tùy chọn',
    general: 'Chung',
    membersTeams: 'Thành viên và nhóm',
    permissions: 'Quyền',
    fileAccess: 'Quyền truy cập tệp',
    myMemories: 'Bộ nhớ của tôi',
    sharedMemories: 'Bộ nhớ chung',
    overview: 'Tổng quan',
    creditActivity: 'Hoạt động tín dụng',
    planCredits: 'Gói và tín dụng',
    assistant: 'Trợ lý',
    memberships: 'Tư cách thành viên',
    notifications: 'Thông báo',
    openNavigation: 'Mở điều hướng cài đặt',
    noAccess: 'Bạn không có quyền truy cập các cài đặt này.',
  },
  id: {
    devices: 'Perangkat',
    invoices: 'Faktur',
    personal: 'Pribadi',
    workspaceScope: 'Ruang kerja',
    preferences: 'Preferensi',
    general: 'Umum',
    membersTeams: 'Anggota & tim',
    permissions: 'Izin',
    fileAccess: 'Akses file',
    myMemories: 'Memori saya',
    sharedMemories: 'Memori bersama',
    overview: 'Ringkasan',
    creditActivity: 'Aktivitas kredit',
    planCredits: 'Paket & kredit',
    assistant: 'Asisten',
    memberships: 'Keanggotaan',
    notifications: 'Notifikasi',
    openNavigation: 'Buka navigasi pengaturan',
    noAccess: 'Anda tidak memiliki akses ke pengaturan ini.',
  },
};

export function getSettingsExperienceCopy(locale: string) {
  return COPY[normalizeLocale(locale)];
}
